import React, { useState, useEffect } from 'react';
import { ASSET_TYPES, ASSET_STATUSES, FLOORS } from '../mockData';
import AssetDetailPanel from '../components/AssetDetailPanel';
import { Search, Filter, Package } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from '../components/ui/sonner';
import AssetIcon from '../components/AssetIcon';
import { useTranslation } from 'react-i18next';

const AssetList = () => {
  const { t } = useTranslation();
  const [assets, setAssets] = useState([]);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [floorFilter, setFloorFilter] = useState('all');

  useEffect(() => {
    const storedAssets = localStorage.getItem('e2-assets');
    if (storedAssets) {
      setAssets(JSON.parse(storedAssets));
    } else {
      const { MOCK_ASSETS } = require('../mockData');
      setAssets(MOCK_ASSETS);
      localStorage.setItem('e2-assets', JSON.stringify(MOCK_ASSETS));
    }
  }, []);

  const filteredAssets = assets.filter(asset => {
    const matchesSearch = asset.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         asset.room.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = typeFilter === 'all' || asset.type === typeFilter;
    const matchesStatus = statusFilter === 'all' || asset.status === statusFilter;
    const matchesFloor = floorFilter === 'all' || asset.floor === parseInt(floorFilter);
    
    return matchesSearch && matchesType && matchesStatus && matchesFloor;
  });

  const handleStatusUpdate = (assetId, newStatus, tickets = null) => {
    setAssets(prev => {
      const updated = prev.map(a => {
        if (a.id === assetId) {
          const updatedAsset = { 
            ...a, 
            status: newStatus, 
            lastSeen: new Date().toISOString() 
          };
          if (tickets !== null) {
            updatedAsset.maintenanceTickets = tickets;
          }
          return updatedAsset;
        }
        return a;
      });
      localStorage.setItem('e2-assets', JSON.stringify(updated));
      return updated;
    });
    
    if (selectedAsset && selectedAsset.id === assetId) {
      const updatedSelectedAsset = { 
        ...selectedAsset, 
        status: newStatus,
        lastSeen: new Date().toISOString()
      };
      if (tickets !== null) {
        updatedSelectedAsset.maintenanceTickets = tickets;
      }
      setSelectedAsset(updatedSelectedAsset);
    }
    
    const statusLabel = ASSET_STATUSES.find(s => s.value === newStatus)?.label;
    toast.success(`Status updated to ${statusLabel}`);
  };

  const handleShowOnMap = (asset) => {
    // Navigation is now handled in AssetDetailPanel
    return;
  };

  const handleReplayPath = (asset) => {
    toast.info(`Replaying movement path for ${asset.name}`);
  };

  const handleFindSimilar = (asset) => {
    const similar = assets.filter(a => 
      a.type === asset.type && 
      a.floor === asset.floor && 
      a.id !== asset.id
    );
    toast.info(`Found ${similar.length} similar ${asset.type}(s) on Floor ${asset.floor}`);
  };

  const getStatusInfo = (status) => ASSET_STATUSES.find(s => s.value === status);
  const getTypeLabel = (type) => ASSET_TYPES.find(t => t.value === type)?.label;
  const getFloorInfo = (floorId) => FLOORS.find(f => f.id === floorId);

  return (
    <div className="asset-list-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">{t('assets.title')}</h1>
          <p className="page-subtitle">{t('assets.subtitle')}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="list-filters">
        <div className="search-box-large">
          <Search size={20} className="search-icon" />
          <Input
            type="text"
            placeholder={t('assets.searchByName')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input-large"
          />
        </div>

        <div className="filter-row">
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="filter-select">
              <SelectValue placeholder={t('assets.allTypes')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('assets.allTypes')}</SelectItem>
              {ASSET_TYPES.map(type => (
                <SelectItem key={type.value} value={type.value}>
                  {t(`assetTypes.${type.value}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="filter-select">
              <SelectValue placeholder={t('assets.allStatuses')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('assets.allStatuses')}</SelectItem>
              {ASSET_STATUSES.map(status => (
                <SelectItem key={status.value} value={status.value}>
                  {t(`status.${status.value.replace(/-/g, '')}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={floorFilter} onValueChange={setFloorFilter}>
            <SelectTrigger className="filter-select">
              <SelectValue placeholder={t('assets.allFloors')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('assets.allFloors')}</SelectItem>
              {FLOORS.map(floor => (
                <SelectItem key={floor.id} value={floor.id.toString()}>
                  {floor.name} - {floor.department}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Results Count */}
      <div className="results-count">
        {t('assets.showingOf', { count: filteredAssets.length, total: assets.length })}
      </div>

      {/* Asset List */}
      <div className="assets-grid">
        {filteredAssets.map(asset => {
          const statusInfo = getStatusInfo(asset.status);
          const floorInfo = getFloorInfo(asset.floor);
          const timeSince = Math.floor((Date.now() - new Date(asset.lastSeen).getTime()) / 60000);

          return (
            <div 
              key={asset.id} 
              className="asset-card"
              onClick={() => setSelectedAsset(asset)}
            >
              <div className="asset-card-header">
                <div className="asset-card-icon" style={{ borderColor: statusInfo.color }}>
                  <AssetIcon type={asset.type} size={22} />
                </div>
                <div className="asset-card-info">
                  <h3 className="asset-card-name">{asset.name}</h3>
                  <p className="asset-card-type">{t(`assetTypes.${asset.type}`)}</p>
                </div>
                <Badge 
                  className="asset-card-badge"
                  style={{ backgroundColor: statusInfo.color }}
                >
                  {t(`status.${asset.status.replace(/-/g, '')}`)}
                </Badge>
              </div>

              <div className="asset-card-details">
                <div className="detail-row">
                  <span className="detail-label">{t('assets.location')}:</span>
                  <span className="detail-value">
                    {floorInfo.name} - {asset.room}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">{t('assets.battery')}:</span>
                  <span 
                    className="detail-value"
                    style={{ 
                      color: asset.batteryLevel < 20 ? '#F45053' : 
                             asset.batteryLevel < 50 ? '#F59E0B' : '#10B981'
                    }}
                  >
                    {asset.batteryLevel}%
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">{t('assets.lastSeen')}:</span>
                  <span className="detail-value">{timeSince}m</span>
                </div>
              </div>

              {asset.maintenanceTickets && asset.maintenanceTickets.length > 0 && (
                <div className="asset-card-alert">
                  <Package size={16} />
                  {asset.maintenanceTickets.length} {t('assets.openTicket')}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {filteredAssets.length === 0 && (
        <div className="empty-state">
          <Filter size={48} className="empty-icon" />
          <h3 className="empty-title">{t('assets.noAssetsFound')}</h3>
          <p className="empty-text">{t('assets.adjustFilters')}</p>
        </div>
      )}

      {/* Asset Detail Panel */}
      {selectedAsset && (
        <AssetDetailPanel
          asset={selectedAsset}
          onClose={() => setSelectedAsset(null)}
          onStatusUpdate={handleStatusUpdate}
          onReplayPath={handleReplayPath}
          onFindSimilar={handleFindSimilar}
        />
      )}
    </div>
  );
};

export default AssetList;
